import React, {useEffect, useState, useRef} from 'react'
import Dropzone from 'react-dropzone'
import {ref, uploadBytesResumable, getDownloadURL} from 'firebase/storage'
import {updateDoc, doc, getDoc, collection, addDoc, serverTimestamp} from 'firebase/firestore'
import db, {storage} from '../../../../services/firebase'
import {useLocation} from 'react-router-dom'
import {useAuth} from '../../../modules/auth'
import FormatTimestamp from 'app/components/FormatTimestamp'

const UploadFile: React.FC<{
  onFileUpload: (file: File[] | null) => void
  fid: any
  testName: string
  selectedTimeframe?: string
  status?: string
  nextUpdateDate?: string
  isOpen?:boolean
}> = ({onFileUpload, fid, testName, selectedTimeframe, status, nextUpdateDate,isOpen}, testId) => {
  const [files, setFiles] = useState<File[]>([])
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [date, setDate] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const TestId = searchParams.get('id')
  const {currentUser} = useAuth()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setFiles([]);
  }, [isOpen, fid]);

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollIntoView({behavior: 'smooth', block: 'end'})
    }
  }

  useEffect(() => {
    const date = new Date()
    setDate(date.toISOString().slice(0, 10))
  }, [])

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('')
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [errorMessage])

  const calculateTotalSize = (files: File[]): number => {
    return files.reduce((totalSize, file) => totalSize + file.size, 0)
  }

  const handleUpload = (acceptedFiles: File[]) => {
    setFiles([...files, ...acceptedFiles])
    setTimeout(scrollToBottom, 100)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    try {
      const totalSizeLimit = 10 * 1024 * 1024
      const totalSize = calculateTotalSize(files)

      if (totalSize > totalSizeLimit) {
        setErrorMessage(
          'Total file size exceeds 10 MB. Please upload files with a total size less than 5 MB.'
        )
        setIsLoading(false)
        return
      }

      const filteredFiles = files

      if (filteredFiles.length === 0) {
        setErrorMessage('Invalid file type. Please select files with supported formats: docx, pdf')
        setIsLoading(false)
        return
      }

      const fileData: {
        name: string
        url: string
        date: string
        size: number
        uploaded_at: any
        uploaded_by: string
        uploaded_uid: string
      }[] = []

      const tenantId = currentUser?.tenantId || ''
      const testId = TestId
      const testDocRef = doc(db, `tenants/${tenantId}/tests/${testId || fid}`)
      const testDocSnapshot = await getDoc(testDocRef)
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      let existingFiles = []
      if (testDocSnapshot.exists()) {
        existingFiles = testDocSnapshot.data().files || []
      }
      const today = new Date()
      const annualDate = new Date(today.setFullYear(today.getFullYear() + 1))
      const nextReviewDate = nextUpdateDate ? nextUpdateDate : annualDate.toISOString()

      for (const file of filteredFiles) {
        const fileName = file.name
        const fileRef = ref(
          storage,
          `tenants/${currentUser?.tenantId}/tests/test_evidence/${fileName}`
        )

        const uploadTask = uploadBytesResumable(fileRef, file)
        const snapshot = await uploadTask
        const downloadURL = await getDownloadURL(snapshot.ref)

        fileData.push({
          name: fileName,
          url: downloadURL,
          date: nextReviewDate,
          size: file.size,
          uploaded_at: new Date().toISOString(),
          uploaded_by: currentUser?.userName ? currentUser.userName.trim() : '',
          uploaded_uid: currentUser?.uid || '',
        })
      }

      const updatedFiles = [...existingFiles, ...fileData]

      await updateDoc(testDocRef, {
        files: updatedFiles,
        status: 'Pass',
        next_review_date: nextReviewDate,
        timeframe: 'Annually',
      })

      const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
      await addDoc(activitiesRef, {
        message: `Test evidence uploaded for ${testName}.`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        action: 'Test updated',
        target: 'Tests',
        email: currentUser?.email,
        date: todaysDate,
      })

      setIsLoading(false)
      setFiles([])
      setTimeout(scrollToBottom, 100)
    } catch (error) {
      setErrorMessage('Error uploading evidence. Please try again.')
      setIsLoading(false)
    }
  }

  const handleClear = (index: number) => {
    const updatedFiles = [...files]
    updatedFiles.splice(index, 1)
    setFiles(updatedFiles)
  }
  const handleAllClear = () => {
    setFiles([])
  }

  const handleFileNameChange = (index: number, newName: string) => {
    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles]
      const file = updatedFiles[index]
      const newFile = new File([file], newName, {type: file.type})
      updatedFiles[index] = newFile
      return updatedFiles
    })
  }

  return (
    <>
      <div className='main-container my-8'>
        <Dropzone
          onDrop={handleUpload}
          minSize={0}
          // maxSize={10 * 1024 * 1024}
          multiple
          accept={{
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/pdf': ['.pdf'],
          }}
          disabled={currentUser?.role === 'Auditor' || status === 'Deactivated'}
        >
          {({getRootProps, getInputProps}) => (
            <div
              {...getRootProps({
                className: 'dropzone',
                style: {
                  backgroundColor: '#fff',
                  padding: '20px',
                  border: '2px dashed #ccc',
                  borderRadius: '4px',
                  textAlign: 'center',
                  cursor:
                    currentUser?.role === 'Auditor' || status === 'Deactivated' 
                      ? 'not-allowed'
                      : 'pointer',
                },
              })}
            >
              <input {...getInputProps()} />
              <img src='/media/icon/uploadFile.svg' alt='' loading='lazy' className='mb-4 mt-4' />
              <p className='fw-bold fs-6'>
                Drag and drop or <span style={{color: '#0e9f6e'}}>select</span> a files
              </p>
              <p className='fs-6'>
                Upload up to 10 MB. Supported formats: jpg, png, csv, xlsx, docx, pdf
              </p>
            </div>
          )}
        </Dropzone>
      </div>

      {errorMessage && (
        <div className='alert alert-dismissible bg-light-danger d-flex flex-column flex-sm-row mt-2'>
          <div className={'d-flex flex-column text-danger text-primary text-bold pe-0 pe-sm-10'}>
            <span>{errorMessage}</span>
          </div>
        </div>
      )}
      {files.map((file, index) => (
        <>
          <div key={index}>
            <div className='d-flex align-items-center mb-4 mt-8'>
              <i className='ki-duotone ki-file fs-1'>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
              <div className='d-flex justify-content-between align-items-center flex-grow-1 '>
                <div className='mx-2 fw-bold fs-5 '>Document #{index + 1}</div>
                <i
                  className='ki-duotone ki-trash fs-1 mx-2 cursor-pointer'
                  onClick={() => handleClear(index)}
                >
                  <span className='path1'></span>
                  <span className='path2'></span>
                  <span className='path3'></span>
                  <span className='path4'></span>
                  <span className='path5'></span>
                </i>
              </div>
            </div>

            <div className='mb-3'>
              <label className='form-label fs-5 fw-bold'>File Name</label>
              <input
                value={file.name}
                className='form-control'
                onChange={(e) => handleFileNameChange(index, e.target.value)}
              />
              <p className='text-gray-700 my-1'>Give your auditor a understandable filename.</p>
            </div>
            <div className='mb-3'>
              <label className='form-label fs-5 fw-bold'>Recurrence Start</label>
              <input value={date} className='form-control' readOnly disabled />
              <p className='text-gray-700 my-1'>Next update on: {FormatTimestamp({timestamp: nextUpdateDate})}</p>
            </div>
            <div className='separator my-5'></div>
          </div>
        </>
      ))}

      {files.length > 0 && (
        <div className='modal-footer' ref={scrollContainerRef}>
          {isLoading ? (
            <button
              type='submit'
              className='btn btn-sm fs-6'
              style={{backgroundColor: '#0E9F6E', color: '#FFFFFF', height: '40px'}}
            >
              Adding...
              <span className='spinner-border spinner-border-sm align-middle mx-1'></span>
            </button>
          ) : (
            <div className='d-flex justify-content-end align-items-center mx-0'>
              <button
                type='button'
                className='btn btn-light btn-sm fs-6 mx-4'
                onClick={handleAllClear}
              >
                Cancel
              </button>
              <div className='' style={{cursor: 'pointer'}} onClick={handleSubmit}>
                <button
                  className='btn btn-sm fs-6'
                  style={{backgroundColor: '#0E9F6E', color: '#FFFFFF', height: '40px'}}
                  onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                    (e.currentTarget.style.backgroundColor = '#148363')
                  }
                  onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                    (e.currentTarget.style.backgroundColor = '#0E9F6E')
                  }
                  disabled={currentUser?.role === 'Auditor'}
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default UploadFile
